/**
 * StaffManagement — the roster and the wage report, under one set of tabs.
 *
 * This file owns the two fetches and which tab is showing; the header, stats,
 * tab bar, roster table and wage panel are their own components.
 */
import { Loader2 } from 'lucide-react';
import { useState, useEffect } from 'react';

import { fetchStaff, fetchWageReport, type StaffMember, type WageReport } from '../../api/staff';

import StaffPageHeader from './components/StaffPageHeader';
import StaffRosterTable from './components/StaffRosterTable';
import StaffStats from './components/StaffStats';
import StaffTabBar, { type StaffTab } from './components/StaffTabBar';
import WageReportPanel from './components/WageReportPanel';

const StaffManagement = () => {
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<StaffTab>('roster');

  // Wage report state
  const [wageReport, setWageReport] = useState<WageReport | null>(null);
  const [wageLoading, setWageLoading] = useState(false);
  const [wageStartDate, setWageStartDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [wageEndDate, setWageEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [wageViewMode, setWageViewMode] = useState<'summary' | 'detail'>('summary');

  useEffect(() => {
    fetchStaff()
      .then(setStaff)
      .catch((err) => console.error('[Staff] Failed to fetch:', err))
      .finally(() => setLoading(false));
  }, []);

  const loadWageReport = () => {
    setWageLoading(true);
    fetchWageReport(wageStartDate, wageEndDate)
      .then(setWageReport)
      .catch((err) => console.error('[Staff] Failed to fetch wage report:', err))
      .finally(() => setWageLoading(false));
  };

  const term = searchTerm.toLowerCase();
  const filteredStaff = searchTerm
    ? staff.filter(
        (s) =>
          s.name.toLowerCase().includes(term) || (s.email?.toLowerCase().includes(term) ?? false),
      )
    : staff;

  return (
    <div className="min-h-screen">
      <StaffPageHeader loading={loading} />

      <div className="max-w-7xl mx-auto px-6 py-6">
        <StaffStats staff={staff} />
        <StaffTabBar
          activeTab={activeTab}
          onTabChange={setActiveTab}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
        />

        {loading ? (
          <div className="bg-white dark:bg-[#1a1f1c] rounded-lg border p-12 flex flex-col items-center justify-center">
            <Loader2 size={32} className="animate-spin text-moss-600 mb-4" />
            <p className="text-stone-600 dark:text-stone-400 font-medium">Loading staff data...</p>
          </div>
        ) : activeTab === 'roster' ? (
          <StaffRosterTable rows={filteredStaff} />
        ) : (
          <WageReportPanel
            startDate={wageStartDate}
            endDate={wageEndDate}
            setStartDate={setWageStartDate}
            setEndDate={setWageEndDate}
            report={wageReport}
            loading={wageLoading}
            onGenerate={loadWageReport}
            viewMode={wageViewMode}
            setViewMode={setWageViewMode}
          />
        )}
      </div>
    </div>
  );
};

export default StaffManagement;
